import { useEffect, useReducer, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, describeApiError, identityFieldsFor, type DiscoverItem, type TransformSystemPropertiesDto } from "../lib/api";
import { Button } from "../components/primitives/Button";
import { FormField, TextInput } from "../components/primitives/FormField";
import { PageHelp } from "../components/PageHelp";
import { SchemaForm } from "../components/SchemaForm";
import { EnrichmentsField } from "../components/schema-form/EnrichmentsField";
import { StateMappingField } from "../components/schema-form/StateMappingField";
import { FilterBuilder } from "../components/FilterBuilder";
import { IdentityPolicyEditor, stripUnmaskable } from "../components/IdentityPolicyEditor";
import type { JsonSchema } from "../lib/schema-form/types";
import {
  initialState,
  wizardReducer,
  type WizardStep,
  type WizardState,
} from "./pipeline-wizard-reducer";
import { modeLabel } from "../lib/mode-labels";
import { transformDescription, vendorLabel } from "../lib/friendly-labels";

// ─── Types ──────────────────────────────────────────────────────────────────

interface Connector {
  connectorType: string;
  label: string;
  capabilities: string[];
  /** Destination properties that are valid identity match keys, keyed by
   *  destination object type (spec §6.8). Feeds the record-adoption editor. */
  identityFields: Record<string, string[]> | null;
  ingest?: {
    webhook?: { signatureScheme: string };
    poll?: object;
    stream?: object;
    sqs?: object;
  };
  documentation?: {
    capabilityDocs?: Record<string, string>;
  } | null;
}

interface Instance {
  id: string;
  name: string;
  connectorType: string;
  organizationId: string;
  // null/undefined = any-env (matches every pipeline environment); a concrete
  // value only matches a pipeline in that same environment (DEV-6562).
  environment?: string | null;
}

interface Transform {
  id: string;
  /** Plain-language name from the registry (spec §4.2). */
  label?: string | null;
  sourceConnectorType: string;
  destConnectorType: string;
  sourceObjectType: string;
  destObjectType: string;
  description: string;
  hasParamsSchema: boolean;
  paramsJsonSchema: JsonSchema | null;
  /** Properties a write mask may never drop — the external key the upsert
   *  matches on, the association keys, and the source-time property the
   *  staleness guard compares against. */
  unmaskableKeys?: string[];
  systemProperties?: TransformSystemPropertiesDto | null;
}

interface Organization { id: string; name: string }
interface Environment { name: string; disabled?: boolean }

type DiscoverState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ok"; items: DiscoverItem[] }
  | { status: "error"; detail: string };

// ─── UI ─────────────────────────────────────────────────────────────────────

/**
 * Build a pipeline one decision at a time: source, mode, transform,
 * destination, params, policy.
 *
 * Arriving as `/pipelines/new?organizationId=<id>` from a client workspace
 * scopes the whole wizard to that client, so the connections offered are only
 * ones the pipeline could actually be built from.
 */
export function PipelineWizard() {
  const nav = useNavigate();
  const [state, dispatch] = useReducer(wizardReducer, initialState);
  const [connectors, setConnectors] = useState<Connector[] | null>(null);
  const [instances, setInstances] = useState<Instance[] | null>(null);
  const [transforms, setTransforms] = useState<Transform[] | null>(null);
  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  // /pipelines/new?organizationId=<id> arrives from a client workspace: only
  // that client's connections are offered as sources and the final form opens
  // with the client preselected.
  const [searchParams] = useSearchParams();
  const [organizationId, setOrganizationId] = useState(searchParams.get("organizationId") ?? "");
  const [name, setName] = useState("");
  const [environment, setEnvironment] = useState("");
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [filter, setFilter] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);
  const [destProperties, setDestProperties] = useState<{ id: string; name: string }[]>([]);
  const [stages, setStages] = useState<DiscoverItem[]>([]);
  const [discover, setDiscover] = useState<DiscoverState>({ status: "idle" });
  // `state.created` only flips once the create RESOLVES, so it cannot cover the
  // window between the click and the response. A ref does: it updates
  // synchronously, whereas a useState guard is not visible until the next
  // render and a fast second submit would read the stale value.
  const createInFlight = useRef(false);
  // Purely the visible affordance — the ref above is what enforces it.
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const safe = <T,>(p: Promise<T>): Promise<T | null> =>
      p.catch((e) => { setError(e instanceof Error ? e.message : String(e)); return null; });
    safe(api.listConnectors()).then((r) => setConnectors(r?.connectors ?? []));
    safe(api.listConnectorInstances()).then((r) => setInstances(r?.instances ?? []));
    safe(api.listTransforms()).then((r) => setTransforms((r?.transforms ?? []) as Transform[]));
    safe(api.listOrganizations()).then((r) => setOrgs(r?.organizations ?? []));
  }, []);

  // Load the selected org's registered environments so the ENVIRONMENT field is a
  // dropdown, not free text — typing an unregistered value (e.g. "dev" instead of
  // "timberwolves-dev") otherwise fails pipeline creation with a generic 422 (DEV-5005).
  useEffect(() => {
    // Clear both the selection and the option list up front so the previous org's
    // environments are never selectable while the new org loads — and stay cleared
    // if the request fails, so a stale name can't be submitted with the new org id.
    setEnvironment("");
    setEnvironments([]);
    if (!organizationId) return;
    let cancelled = false;
    api
      .listEnvironments({ organizationId })
      .then((r) => { if (!cancelled) setEnvironments((r?.environments ?? []) as Environment[]); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [organizationId]);

  // Fire HubSpot contact property discovery once when the operator enters the
  // params step for a HubSpot destination. Pure reference data — the operator
  // copies property ids from this list into propertyAliases values.
  useEffect(() => {
    if (state.step !== "params") return;
    if (state.destConnectorType !== "hubspot") return;
    if (!state.destInstanceId) return;
    setDiscover({ status: "loading" });
    api
      .discover(state.destInstanceId, "properties:contact")
      .then((r) => setDiscover({ status: "ok", items: r.items ?? [] }))
      .catch((e: unknown) => {
        const detail = e instanceof Error ? e.message : String(e);
        setDiscover({ status: "error", detail });
      });
  }, [state.step, state.destConnectorType, state.destInstanceId]);

  // The destination's own properties, which seed the record-adoption write-mask
  // groups. A destination we can't reach leaves the groups empty rather than
  // blocking the step — the API validates the mask on save either way.
  useEffect(() => {
    setDestProperties([]);
    if (state.step !== "params") return;
    const t = transforms?.find((x) => x.id === state.transformId);
    const instanceId = state.destInstanceId;
    if (!t?.destObjectType || !instanceId) return;
    // The discover kind takes the SHORT object name (`properties:deal`).
    const shortType = t.destObjectType.slice(t.destObjectType.indexOf(".") + 1);
    let cancelled = false;
    api
      .discover(instanceId, `properties:${shortType}`)
      .then((r) => { if (!cancelled) setDestProperties(r.items ?? []); })
      .catch(() => { if (!cancelled) setDestProperties([]); });
    return () => { cancelled = true; };
  }, [state.step, state.transformId, state.destInstanceId, transforms]);

  // The stage picker's options (spec §7 UI). Only fetched for a transform that
  // actually has a stage to set, and best-effort: on a failure the widget
  // degrades to free text and the save-time check warns.
  //
  // Clears FIRST, on every run and before every early return — the wizard is
  // one mounted component in which an operator changes destination instance
  // and transform freely, so without that clear the picker keeps offering the
  // previous portal's stage ids after the selection moves, and offers stale
  // ids indefinitely after switching to a transform that has no stage at all.
  useEffect(() => {
    setStages([]);
    const t = transforms?.find((x) => x.id === state.transformId);
    const instanceId = state.destInstanceId;
    if (!instanceId || !t?.systemProperties?.stateField) return;
    let cancelled = false;
    api
      .discover(instanceId, "dealPipelines")
      .then((r) => { if (!cancelled) setStages(r.items ?? []); })
      .catch(() => { if (!cancelled) setStages([]); });
    return () => { cancelled = true; };
  }, [state.destInstanceId, state.transformId, transforms]);

  if (connectors === null || instances === null || transforms === null || orgs === null) {
    return (
      <div style={{ maxWidth: 800 }}>
        <div className="uppercase-label" style={{ marginBottom: 16 }}>NEW PIPELINE</div>
        <PageHelp />
        {error ? (
          <p className="page-help">Couldn't load wizard data: {error}</p>
        ) : (
          <p className="page-help">Loading…</p>
        )}
      </div>
    );
  }

  const sourceConnector = connectors.find((c) => c.connectorType === state.sourceConnectorType);
  const sourceCandidates = instances.filter((i) => {
    if (organizationId && i.organizationId !== organizationId) return false;
    const c = connectors.find((cc) => cc.connectorType === i.connectorType);
    return c && c.capabilities.some((cap) => cap.startsWith("ingest."));
  });

  const sourceModes: Array<"webhook" | "poll" | "stream" | "sqs"> = sourceConnector
    ? (["webhook", "poll", "stream", "sqs"] as const).filter(
        (m) => Boolean(sourceConnector.ingest?.[m]),
      )
    : [];

  const matchingTransforms = transforms.filter(
    (t) => state.sourceConnectorType && t.sourceConnectorType === state.sourceConnectorType,
  );

  // The org isn't picked until the final step, but the source connection (chosen
  // in step 1) already fixes it — the pipeline shares its source's org. Scope the
  // dest + enrichment pickers to that org so only compatible connections are
  // offered; a cross-org connection is denied at runtime and fails every event
  // with a misleading "found no value" (DEV-6380).
  //
  // The pipeline's environment, by contrast, is chosen last (the "done" step) —
  // there's no environment to scope against yet here, so these pickers are
  // org-scoped only. Env-compatibility is enforced at save time (a mismatched
  // pick is rejected with a 422, DEV-6562) and, for edits, by PipelineEditor's
  // picker, which does know the pipeline's environment.
  // TODO(DEV-6562 follow-up): move environment selection earlier in the wizard
  // so these pickers can be env-scoped too.
  const sourceInstance = (instances ?? []).find((i: Instance) => i.id === state.sourceInstanceId);
  const wizardOrgId = sourceInstance?.organizationId ?? "";

  const destCandidates = instances.filter((i) => {
    if (!state.destConnectorType) return false;
    if (i.connectorType !== state.destConnectorType) return false;
    const c = connectors.find((cc) => cc.connectorType === i.connectorType);
    return c?.capabilities.includes("deliver");
  });

  const selectedTransform = transforms.find((t) => t.id === state.transformId) ?? null;

  const enrichmentWidgetContext = {
    connections: (instances ?? [])
      .filter((i: Instance) => wizardOrgId !== "" && i.organizationId === wizardOrgId)
      .map((i: Instance) => ({ id: i.id, name: i.name, connectorType: i.connectorType, environment: i.environment })),
    // `api.discover` is a stable module-level reference — pass it directly rather
    // than a fresh closure, so DiscoverInput's fetch effect (keyed on `discover`)
    // doesn't re-fire on every keystroke/re-render.
    discover: api.discover,
    stateMapping: {
      stateField: selectedTransform?.systemProperties?.stateField,
      canArchive: selectedTransform?.systemProperties?.canArchive,
      destObjectType: selectedTransform?.destObjectType,
      stages,
      defaultStateStages: selectedTransform?.systemProperties?.defaultStateStages,
      // A new pipeline has no legacy stage map to fold in — this is normally
      // undefined here, unlike the editor's pre-existing pipeline.
      legacyStageMap: (state.params as { dealStageByStatus?: Record<string, string> }).dealStageByStatus,
      // Adoption is answered further down the same step (`adoptionSection`), so
      // the caution tracks it live.
      adoptionEnabled: state.identityPolicy != null,
    },
  };

  // Record adoption sits with the transform params: it is the other half of
  // "what lands at the destination", and both are answered against the same
  // destination object type.
  const adoptionSection = (
    <>
      <div className="uppercase-label" style={{ margin: "24px 0 8px" }}>RECORD ADOPTION</div>
      <IdentityPolicyEditor
        value={state.identityPolicy}
        onChange={(v) => dispatch({ type: "setIdentityPolicy", identityPolicy: v })}
        destObjectType={selectedTransform?.destObjectType}
        identityFields={identityFieldsFor(connectors, state.destConnectorType ?? undefined, selectedTransform?.destObjectType)}
        connections={enrichmentWidgetContext.connections}
        discover={api.discover}
        destProperties={destProperties}
        unmaskableKeys={selectedTransform?.unmaskableKeys ?? []}
      />
    </>
  );

  /** Create the pipeline, guarded against a second click minting a duplicate. */
  async function submit(e: FormEvent) {
    e.preventDefault();
    // The create already went through. A create that returns warnings keeps the
    // operator on this page, and if it came back without a pipeline body there
    // is no id to swap the button for a link — so without this guard a second
    // click posts the same body again and mints a duplicate pipeline.
    if (state.created || createInFlight.current) return;
    createInFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const trigger: Record<string, unknown> = { mode: state.mode };
      if (state.mode === "poll") trigger.intervalSec = state.pollIntervalSec;
      const body = {
        organizationId,
        name,
        environment,
        sourceInstanceId: state.sourceInstanceId!,
        destInstanceId: state.destInstanceId!,
        triggers: [trigger],
        transformId: state.transformId!,
        transformParams: state.params,
        conflictPolicy: { defaultTier: state.policy! },
        // Symmetry with PipelineEditForm. A mask built here cannot contain an
        // unmaskable key — the editor composes it from `writable` — so this is
        // a no-op today. It is here so the two submit paths are the same
        // gesture: an asymmetry nobody can see is one the next caller inherits.
        identityPolicy: stripUnmaskable(state.identityPolicy, selectedTransform?.unmaskableKeys ?? []),
        filter,
        enabled: true,
      };
      const res = await api.createPipeline(body);
      // Warnings mean the pipeline was created but a check couldn't run. Stay
      // put and say so — navigating away would throw them out unread.
      if (res?.warnings?.length) {
        dispatch({ type: "created", createdId: res.pipeline?.id ?? null, warnings: res.warnings });
      } else {
        nav("/pipelines");
      }
    } catch (err: unknown) {
      // See PipelineEditor: the 422 message alone names nothing actionable.
      setError(describeApiError(err));
    } finally {
      // Released on both paths: a failed create has to be retryable, and after a
      // warnings-create `state.created` is what keeps the guard closed.
      createInFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <div style={{ maxWidth: 1100 }}>
      <div className="uppercase-label" style={{ marginBottom: 16 }}>NEW PIPELINE</div>
      <PageHelp />

      <div className="wizard-steps">
        {(["source", "mode", "transform", "dest", "params", "policy"] as const).map((s, i) => {
          const cls = state.step === s ? "is-active" : isStepDone(state, s) ? "is-done" : "";
          return <div key={s} className={`wizard-step ${cls}`}>{i + 1}. {labelForStep(s)}</div>;
        })}
      </div>

      {state.step === "source" && (
        <StepBlock title="PICK A SOURCE">
          {sourceCandidates.length === 0 ? (
            <p className="page-help">No connector instances with ingest capability yet. Create one in CONNECTIONS first.</p>
          ) : sourceCandidates.map((i) => (
            <button
              key={i.id}
              type="button"
              className={`wizard-option${state.sourceInstanceId === i.id ? " is-selected" : ""}`}
              onClick={() => dispatch({ type: "pickSource", instanceId: i.id, connectorType: i.connectorType })}
            >
              <div className="wizard-option__title">{vendorLabel(i.connectorType, connectors)} / {i.name}</div>
              <div className="wizard-option__hint mono">{i.connectorType}</div>
            </button>
          ))}
        </StepBlock>
      )}

      {state.step === "mode" && (
        <StepBlock title="PICK AN INGEST MODE">
          {sourceModes.map((m) => (
            <button
              key={m}
              type="button"
              className={`wizard-option${state.mode === m ? " is-selected" : ""}`}
              onClick={() => dispatch({ type: "pickMode", mode: m })}
            >
              <div className="wizard-option__title">{modeLabel(m)}</div>
              <div className="wizard-option__hint">
                {sourceConnector?.documentation?.capabilityDocs?.[`ingest.${m}`] ?? ""}
              </div>
            </button>
          ))}
          {state.mode === "poll" && (
            <FormField label="POLL INTERVAL (SECONDS) *" hint="How often to check the source for changes.">
              <TextInput
                type="number"
                value={state.pollIntervalSec}
                onChange={(e) => dispatch({ type: "pickMode", mode: "poll", pollIntervalSec: Number(e.target.value) })}
                min={30}
              />
            </FormField>
          )}
        </StepBlock>
      )}

      {state.step === "transform" && (
        <StepBlock title="PICK A TRANSFORM">
          {matchingTransforms.length === 0 ? (
            <p className="page-help">No transforms registered for source type {state.sourceConnectorType}.</p>
          ) : matchingTransforms.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`wizard-option${state.transformId === t.id ? " is-selected" : ""}`}
              onClick={() => dispatch({ type: "pickTransform", transformId: t.id, destConnectorType: t.destConnectorType })}
            >
              <div className="wizard-option__title">{t.label ?? transformDescription(t.id, transforms)}</div>
              <div className="wizard-option__hint">
                <span className="mono">{t.id}</span>
                {t.description ? ` — ${t.description}` : ""}
              </div>
            </button>
          ))}
        </StepBlock>
      )}

      {state.step === "dest" && (
        <StepBlock title="PICK A DESTINATION">
          {destCandidates.length === 0 ? (
            <p className="page-help">
              No {state.destConnectorType} instances with deliver capability. Create one in CONNECTIONS first.
            </p>
          ) : destCandidates.map((i) => (
            <button
              key={i.id}
              type="button"
              className={`wizard-option${state.destInstanceId === i.id ? " is-selected" : ""}`}
              onClick={() => dispatch({ type: "pickDest", instanceId: i.id })}
            >
              <div className="wizard-option__title">{vendorLabel(i.connectorType, connectors)} / {i.name}</div>
            </button>
          ))}
        </StepBlock>
      )}

      {state.step === "params" && (
        <StepBlock title="CONFIGURE TRANSFORM PARAMS">
          {!selectedTransform ? (
            <p className="page-help">No transform selected.</p>
          ) : !selectedTransform.paramsJsonSchema ? (
            <>
              <p className="page-help">This transform has no configurable params.</p>
              {adoptionSection}
              <Button type="button" variant="primary" onClick={() => dispatch({ type: "confirmParams" })}>
                CONTINUE
              </Button>
            </>
          ) : state.destConnectorType === "hubspot" ? (
            <div className="params-layout">
              <div>
                <SchemaForm
                  schema={selectedTransform.paramsJsonSchema}
                  value={state.params}
                  onChange={(p) => dispatch({ type: "setParams", params: p as Record<string, unknown> })}
                  widgets={{ enrichments: EnrichmentsField, stateMapping: StateMappingField }}
                  widgetContext={enrichmentWidgetContext}
                />
                {adoptionSection}
                <Button type="button" variant="primary" onClick={() => dispatch({ type: "confirmParams" })}>
                  CONTINUE
                </Button>
              </div>
              <HubspotPropertiesAside state={discover} />
            </div>
          ) : (
            <div>
              <SchemaForm
                schema={selectedTransform.paramsJsonSchema}
                value={state.params}
                onChange={(p) => dispatch({ type: "setParams", params: p as Record<string, unknown> })}
                widgets={{ enrichments: EnrichmentsField, stateMapping: StateMappingField }}
                widgetContext={enrichmentWidgetContext}
              />
              {adoptionSection}
              <Button type="button" variant="primary" onClick={() => dispatch({ type: "confirmParams" })}>
                CONTINUE
              </Button>
            </div>
          )}
        </StepBlock>
      )}

      {state.step === "policy" && (
        <StepBlock title="PICK A CONFLICT POLICY">
          {(["source-wins", "dest-wins"] as const).map((p) => (
            <button
              key={p}
              type="button"
              className={`wizard-option${state.policy === p ? " is-selected" : ""}`}
              onClick={() => dispatch({ type: "pickPolicy", policy: p })}
            >
              <div className="wizard-option__title">{p.toUpperCase()}</div>
              <div className="wizard-option__hint">
                {p === "source-wins"
                  ? "On conflict, the source's value overwrites the destination's."
                  : "On conflict, the destination's existing value is preserved; the source value is ignored."}
              </div>
            </button>
          ))}
        </StepBlock>
      )}

      {state.step === "done" && state.warnings.length > 0 && (
        <div className="save-notice" role="status">
          <div className="save-notice__head">Created. Some checks couldn't run — the pipeline will still try them when it runs.</div>
          <ul className="save-notice__list">
            {state.warnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        </div>
      )}

      {state.step === "done" && (
        <form onSubmit={submit} className="wizard-options">
          <div className="uppercase-label" style={{ marginBottom: 8 }}>NAME + SCOPE</div>
          <FormField label="CLIENT *">
            <select
              className="field__input"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              required
            >
              <option value="">—</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </FormField>
          <FormField label="NAME *" hint="Operator-readable name, e.g. denver-hubspot-to-jump">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} required />
          </FormField>
          <FormField label="ENVIRONMENT *" hint="An environment registered for this organization.">
            {!organizationId ? (
              <select className="field__input" disabled aria-label="environment">
                <option value="">Select an organization first</option>
              </select>
            ) : environments.filter((e) => !e.disabled).length === 0 ? (
              <div className="field__hint">No environments registered for this org — create one on the <Link to="/environments">Environments page</Link> first.</div>
            ) : (
              <select
                className="field__input"
                aria-label="environment"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
                required
              >
                <option value="">—</option>
                {environments.filter((e) => !e.disabled).map((e) => (
                  <option key={e.name} value={e.name}>{e.name}</option>
                ))}
              </select>
            )}
          </FormField>

          <div className="uppercase-label" style={{ margin: "16px 0 8px" }}>FILTER</div>
          <FilterBuilder value={filter} onChange={setFilter} sourceObjectType={selectedTransform?.sourceObjectType} destObjectType={selectedTransform?.destObjectType} />

          {error && <div className="field__error" style={{ marginBottom: 12 }}>{error}</div>}
          <div style={{ display: "flex", gap: 12 }}>
            {state.created ? (
              // The pipeline exists; the only thing left is to go look at it.
              // Keyed on `created`, not `createdId`: a warnings response can omit
              // the pipeline body, and re-showing CREATE PIPELINE there would
              // invite a duplicate.
              state.createdId ? (
                <Link className="btn btn--primary" to={`/pipelines/${state.createdId}`}>VIEW PIPELINE</Link>
              ) : (
                <Link className="btn btn--primary" to="/pipelines">VIEW PIPELINES</Link>
              )
            ) : (
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? "CREATING…" : "CREATE PIPELINE"}
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={() => dispatch({ type: "goto", step: "source" })}>
              START OVER
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

/** The destination's real property list, read live, so a mapping is written
 *  against what HubSpot actually has rather than what it is assumed to have. */
function HubspotPropertiesAside({ state }: { state: DiscoverState }) {
  return (
    <aside className="discover-aside">
      <h3 className="discover-aside__title">HUBSPOT CONTACT PROPERTIES — LIVE</h3>
      {state.status === "idle" && (
        <div className="page-help" style={{ margin: 0 }}>Waiting…</div>
      )}
      {state.status === "loading" && (
        <div className="uppercase-label">DISCOVERING…</div>
      )}
      {state.status === "error" && (
        <div className="field__error">Couldn't reach HubSpot for property discovery: {state.detail}</div>
      )}
      {state.status === "ok" && state.items.length === 0 && (
        <div className="page-help" style={{ margin: 0 }}>No properties returned.</div>
      )}
      {state.status === "ok" && state.items.length > 0 && (
        <div className="discover-aside__list" role="list">
          {state.items.map((item) => (
            <div key={item.id} className="discover-aside__row" role="listitem">
              <span className="discover-aside__id">{item.id}</span>
              <span className="discover-aside__meta">{item.description ?? ""}</span>
            </div>
          ))}
        </div>
      )}
    </aside>
  );
}

/** One titled step of the wizard and its options. */
function StepBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="uppercase-label" style={{ marginBottom: 8 }}>{title}</div>
      <div className="wizard-options">{children}</div>
    </div>
  );
}

/** A step's name for the progress rail. */
function labelForStep(s: WizardStep): string {
  switch (s) {
    case "source": return "SOURCE";
    case "mode": return "MODE";
    case "transform": return "TRANSFORM";
    case "dest": return "DESTINATION";
    case "params": return "PARAMS";
    case "policy": return "POLICY";
    case "done": return "DONE";
  }
}

/** Whether a step sits behind the current one, so the rail can mark it done
 *  and offer it as a place to go back to. */
function isStepDone(s: WizardState, step: WizardStep): boolean {
  const order: WizardStep[] = ["source", "mode", "transform", "dest", "params", "policy", "done"];
  return order.indexOf(step) < order.indexOf(s.step);
}
